import {reassessmentIdentity, sameAssessment} from "./reassessmentRecord";
import { conversionSeasonPolicy } from "./conversionSeasonPolicy";
import {
  futureSummary,
  editResult,
  historySummary,
  conversionObservation,
} from "./teamAnalysis";
import {
  proposalEvidence,
  normalizeWeights,
  weightedProposal,
} from "./teamProposal";
import { statisticAdjustmentRange } from "./teamAdjustment";
import { guidedPool } from "./guidedTeamAnalysis";

export const resultCoordinates = [
  "for_home",
  "against_home",
  "for_away",
  "against_away",
];
export const statisticCoordinates = [
  "home_for",
  "home_against",
  "away_for",
  "away_against",
];
export const reviewProfiles = {
  balanced: {
    name: "KI · ausgewogen",
    en: "AI · balanced",
    bias: {},
    pace: 25,
  },
  josy: {
    name: "Josy",
    en: "Josy",
    bias: { should: 1.15, recent: 1.1 },
    pace: 25,
  },
  vince: {
    name: "Vince-All-In",
    en: "Vince-All-In",
    bias: { recent: 1.2, previous: 0.8 },
    pace: 40,
  },
  grandpa: {
    name: "Wettopa",
    en: "Wettopa",
    bias: { previous: 1.2, recent: 0.8 },
    pace: 15,
  },
  unlucky: {
    name: "Unlucky Expert",
    en: "Unlucky Expert",
    bias: { should: 1.2, real: 0.8 },
    pace: 25,
  },
};
export function profileWeights(
  profile,
  preferences = {},
  random = Math.random
) {
  const p = reviewProfiles[profile] || reviewProfiles.balanced;
  const keys = [
    "should",
    "real",
    "market",
    "recent",
    "other",
    "previous",
    "external",
  ];
  return normalizeWeights(
    Object.fromEntries(
      keys.map((k) => [
        k,
        (preferences[k] ?? 1) * (p.bias[k] || 1) * (0.95 + 0.1 * random()),
      ])
    ),
    keys
  );
}
// Colours describe stronger/weaker, never merely the arithmetic sign.
export function settingDirection(field, before, after, group = "") {
  if (
    !Number.isFinite(before) ||
    !Number.isFinite(after) ||
    Math.abs(after - before) < 1e-10
  )
    return 0;
  if (
    [
      "draw_factor",
      "total_goals",
      "for_homefactor",
      "against_homefactor",
      "home_advantage",
    ].includes(field)
  )
    return null;
  const against = field.startsWith("against") || field.endsWith("against");
  // Fouls/cards/offsides are not automatically positive attacking performance.
  if (
    group &&
    group !== "conversion" &&
    ![
      "34",
      "42",
      "43",
      "44",
      "45",
      "49",
      "47",
      "54",
      "58",
      "98",
      "99",
      "86",
    ].includes(String(group))
  )
    return null;
  return Math.sign(after - before) * (against ? -1 : 1);
}
export function resultReassessment(data, id, weights, pace, recent = 8) {
  let row = futureSummary(data.result_teams?.[id]);
  if (!row) return { row: null, rows: [] };
  const identity = reassessmentIdentity(data, id, {weights:Object.fromEntries(Object.entries(weights).filter(([,v]) => v > 0)), pace, recent, model:"result-v2"});
  const record = data.result_teams?.[id]?.reassessment;
  const base = record?.baseline || data.result_teams?.[id];
  const repeat = sameAssessment(record, identity);
  const rows = resultCoordinates.map((field) => {
    const evidence = proposalEvidence(
      data,
      id,
      "result",
      "strength",
      recent,
      field
    );
    const absolute = {...evidence, saved:base[field]};
    const proposed = weightedProposal(absolute, weights, pace / 100);
    const value = repeat ? row[field] : proposed?.target ?? row[field];
    const before = row[field];
    row = editResult(row, field, value);
    return { field, before, value, evidence: absolute, available: !!proposed, assessment: identity };
  });
  if (Number.isFinite(row.draw_factor))
    rows.push({
      field: "draw_factor",
      before: row.draw_factor,
      value: repeat || pace === 0 ? row.draw_factor : drawReassessment(data, id, base.draw_factor).value,
      draw: {...drawReassessment(data, id, base.draw_factor), value:repeat ? row.draw_factor : drawReassessment(data, id, base.draw_factor).value},
      available: drawReassessment(data, id, row.draw_factor).count > 0,
    });
  return { row, rows };
}
// Selected retention changes the shared review identity for BOTH statistics and
// conversion. Existing unselected receipts retain their original configuration.
function statisticReviewIdentity(data,id,pace,preserve){
  return reassessmentIdentity(data,id,preserve
    ? {pace,model:"statistics-v3",preserve:true}
    : {pace,model:"statistics-v2"});
}
export function statisticReassessment(data, id, pace, preserve = data.state?.teams?.[id]?.conversion_season_policy?.preserve === true) {
  const games = guidedPool(data, id),
    team = data.state?.teams?.[id];
  const identity = statisticReviewIdentity(data, id, pace, preserve);
  const record = team?.reassessment;
  const baseline = record?.baseline || team;
  const repeat = sameAssessment(record, identity);
  return Object.entries(team?.statistics || {}).flatMap(
    ([statistic_id, values]) =>
      statisticCoordinates.map((field) => {
        const before = values[field],
          range = statisticAdjustmentRange(
            games,
            id,
            statistic_id,
            field,
            baseline?.statistics?.[statistic_id]?.[field] ?? before
          );
        return {
          group: "statistic",
          statistic_id,
          field,
          before,
          value: repeat ? before : range
            ? range.stored + ((range.endpoint - range.stored) * pace) / 100
            : before,
          assessment: identity,
          range,
          previous: priorStatistic(data, id, statistic_id, field),
        };
      })
  );
}
export function priorStatistic(data, id, key, field) {
  const prior = data.history?.teams?.find(
    (t) => Number(t.team_id) === Number(id)
  )?.sources?.[0];
  return prior?.individual_statistics?.[key]?.[field] ?? null;
}
export function pointContext(data, id, source) {
  const games = guidedPool(data, id).filter(
    (g) =>
      historySummary([g], id, source).outcomes &&
      historySummary([g], id, "real").outcomes
  );
  const expected = historySummary(games, id, source).outcomes,
    actual = historySummary(games, id, "real").outcomes;
  return {
    count: games.length,
    expected: expected?.points ?? null,
    actual: actual?.points ?? null,
  };
}

// Approved conversion rule. Rates are fractions; no additional progression or cap.
export function moderateConversion(standard, observed, count, saved = standard, preserve = false) {
  if (![standard, observed, count, saved].every(Number.isFinite) ||
      standard <= 0 || saved <= 0 || observed < 0 || count <= 0) return null;
  const endpoint = standard + (preserve ? 1 : .25) * (saved - standard)
    + count / (count + 40) * (observed - standard);
  return {standard, observed, count, saved, endpoint, value: endpoint};
}
export function conversionReassessment(data, id, pace, preserve = data.state?.teams?.[id]?.conversion_season_policy?.preserve === true) {
  const games = guidedPool(data, id),
    team = data.state?.teams?.[id];
  const identity = statisticReviewIdentity(data, id, pace, preserve);
  const record = team?.reassessment;
  const repeat = sameAssessment(record, identity);
  const policy = conversionSeasonPolicy(data.state, id, preserve);
  return statisticCoordinates.flatMap((field) => {
    const before = team?.conversion?.[field];
    if (!Number.isFinite(before)) return [];
    const reference = record?.reference?.conversion?.[field] ?? data.state?.reference?.conversion?.[field];
    const baseline = record?.baseline?.conversion?.[field] ?? before;
    const actual = conversionObservation(games, id, field),
      expected = conversionObservation(games, id, field, true);
    const suggestion = moderateConversion(
      reference,
      actual.mean,
      actual.count,
      record?.baseline?.conversion?.[field] ?? before,
      preserve
    );
    return [
      {
        group: "conversion",
        statistic_id: "conversion",
        field,
        before,
        value: repeat ? before : suggestion?.value ?? before,
        assessment: identity,
        baseline,
        rawProposal: suggestion?.value ?? before,
        preserve,
        original: policy?.original[field],
        range: {
          observed: actual.mean,
          expected: expected.mean,
          count: actual.count,
          endpoint: suggestion?.endpoint ?? before,
        },
        standard: reference,
        available: !!suggestion,
        previous: priorStatistic(data, id, "conversion", field),
      },
    ];
  });
}

// Heuristic, not a significance test. Ten matches, 4 vs 2.48 draws:
// .88 - (4 - 2.48)/(10 + 140) = .8698667. Fixed baseline on every proposal.
export function drawReassessment(data, id, before) {
  const games = guidedPool(data, id).filter(
    (g) =>
      historySummary([g], id, "real").outcomes &&
      historySummary([g], id, "result").outcomes
  );
  const actual = historySummary(games, id, "real").outcomes;
  const expected = historySummary(games, id, "result").outcomes;
  const count = games.length;
  if (!count || !Number.isFinite(before) || before <= 0)
    return { before, value: before, count: 0, actual: null, expected: null };
  const delta = Math.max(
    -0.02,
    Math.min(0.02, -(actual.draw - expected.draw) / (count + 140))
  );
  return {
    before,
    value: Math.max(0.01, before + delta),
    count,
    actual: actual.draw,
    expected: expected.draw,
  };
}
